import { useEffect, useRef, useState, type ReactNode } from 'react'

/**
 * The console's shared shapes, as board 08 sets them.
 *
 * The operator's surfaces are a different temperament from the reader's:
 * dense, information-first, unglamorous. Nothing rounds a number, nothing
 * hides a traceback, and a thing that does not exist yet says so rather than
 * being omitted. These pieces exist so that rule is expressed once.
 */

/** A console screen's head: a tracked cap title, a hairline to the edge, a note. */
export function ConsoleHead({
  title,
  note,
  actions,
}: {
  title: string
  note?: ReactNode
  actions?: ReactNode
}) {
  return (
    <div className="mb-3 flex flex-wrap items-center gap-3">
      <h1 className="chrome flex-none text-[11px]">{title}</h1>
      <div className="h-px min-w-[20px] flex-1 bg-n300" />
      {note && (
        <div className="flex-none font-mono text-[10px] uppercase tracking-[0.08em] text-ink2">
          {note}
        </div>
      )}
      {actions && <div className="flex flex-none flex-wrap items-center gap-1.5">{actions}</div>}
    </div>
  )
}

/** The one boxed component on these screens. A neutral-400 hairline, as the board draws it. */
export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <section
      className={`overflow-hidden rounded-[--radius-md] border border-rule2 bg-paper ${className}`}
    >
      {children}
    </section>
  )
}

/** A card's header strip. */
export function CardBar({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={`flex flex-wrap items-center gap-3 border-b border-n300 px-[18px] py-3.5 ${className}`}
    >
      {children}
    </div>
  )
}

/** A card's mono footer line. */
export function CardFoot({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-3.5 border-t border-n300 px-[18px] py-3 font-mono text-[10px] text-ink2">
      {children}
    </div>
  )
}

/** The mono paragraph a board prints under a card, saying what the screen is. */
export function CardNote({ children }: { children: ReactNode }) {
  return (
    <p className="mt-2.5 max-w-[86ch] font-mono text-[10px] leading-[1.7] text-ink2">{children}</p>
  )
}

/** A cell in the stats strip under a chart: a tracked mono label, a large mono figure. */
export function StatCell({ label, value, tone }: { label: string; value: ReactNode; tone?: 'mark' }) {
  return (
    <div className="border-r border-n300 px-4 py-3.5 last:border-r-0">
      <div className="mb-[5px] font-mono text-[9.5px] uppercase tracking-[0.1em] text-ink2">
        {label}
      </div>
      <div
        className={`font-mono text-[19px] leading-tight ${tone === 'mark' ? 'text-mark' : 'text-body'}`}
      >
        {value}
      </div>
    </div>
  )
}

/**
 * A filled or empty square: the matrix's one mark.
 *
 * Colour is never the only cue here either. The square is filled or hollow,
 * and every cell carries the permission and role in its accessible name.
 */
export function Square({ on, label }: { on: boolean; label?: string }) {
  return (
    <span
      role="img"
      aria-label={label ?? (on ? 'granted' : 'not granted')}
      title={label}
      className="inline-block h-[11px] w-[11px] align-middle"
      style={on ? { background: 'var(--jl-accent)' } : { border: '1px solid var(--color-neutral-300)' }}
    />
  )
}

/** A drawn ellipsis. The vendored mono subset has no U+22EF, so it is three dots. */
export function Ellipsis() {
  return (
    <span aria-hidden="true" className="inline-flex items-center gap-[2px]">
      <span className="h-[2px] w-[2px] rounded-full bg-current" />
      <span className="h-[2px] w-[2px] rounded-full bg-current" />
      <span className="h-[2px] w-[2px] rounded-full bg-current" />
    </span>
  )
}

/** The row menu: a dot trigger and a panel that closes on Escape or a click outside. */
export function RowMenu({ label, children }: { label: string; children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function away(event: MouseEvent) {
      if (box.current && !box.current.contains(event.target as Node)) setOpen(false)
    }
    function key(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', away)
    document.addEventListener('keydown', key)
    return () => {
      document.removeEventListener('mousedown', away)
      document.removeEventListener('keydown', key)
    }
  }, [open])

  return (
    <div ref={box} className="relative">
      <button
        type="button"
        aria-label={label}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="rounded-[--radius-sm] px-1.5 py-1 text-ink2 hover:text-body"
      >
        <Ellipsis />
      </button>
      {open && (
        <div className="popover absolute right-0 top-full z-20 mt-1 max-h-[300px] w-[250px] overflow-y-auto p-1.5 text-left">
          {children}
        </div>
      )}
    </div>
  )
}

/** A row inside a RowMenu. */
export function MenuItem({
  children,
  onClick,
  tone,
  disabled,
  trailing,
}: {
  children: ReactNode
  onClick: () => void
  tone?: 'mark'
  disabled?: boolean
  /** A second, narrower action on the same row: the term-limited variant. */
  trailing?: { label: string; title: string; onClick: () => void }
}) {
  const item = (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`block w-full rounded-[--radius-sm] px-2.5 py-1.5 text-left text-[13.5px] hover:bg-surface disabled:opacity-45 ${
        tone === 'mark' ? 'text-mark' : 'text-body'
      }`}
    >
      {children}
    </button>
  )
  if (!trailing) return item
  return (
    <div className="flex items-center gap-1">
      <span className="min-w-0 flex-1">{item}</span>
      <button
        type="button"
        title={trailing.title}
        onClick={trailing.onClick}
        className="flex-none rounded-[--radius-sm] border border-rule2 px-1.5 py-[3px] font-mono text-[10px] text-ink2 hover:text-body"
      >
        {trailing.label}
      </button>
    </div>
  )
}

/**
 * A tuning slider: the mono key, the track, the value in mono.
 *
 * The number is always beside the track, because a weight of 1.4 is a figure
 * an administrator writes down, not a position they eyeball.
 */
export function SliderRow({
  label,
  value,
  min,
  max,
  step,
  onChange,
  disabled,
}: {
  label: string
  value: number
  min: number
  max: number
  step: number
  onChange: (next: number) => void
  disabled?: boolean
}) {
  const id = `tune-${label.replace(/[^a-z0-9]+/gi, '-')}`
  return (
    <div className="flex items-center gap-[11px]">
      <label htmlFor={id} className="min-w-0 flex-1 truncate font-mono text-[11.5px] text-ink2">
        {label}
      </label>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(Number(event.target.value))}
        className="h-[5px] w-24 flex-none cursor-pointer bg-n300 disabled:opacity-45"
        style={{ accentColor: 'var(--jl-accent)' }}
      />
      <div className="w-[46px] flex-none text-right font-mono text-[12.5px]">{value}</div>
    </div>
  )
}

/** A labelled field on the licence form. */
export function Field({
  label,
  children,
  hint,
}: {
  label: string
  children: ReactNode
  hint?: string
}) {
  return (
    <div>
      <div className="mb-[5px] text-[12.5px] text-ink2">{label}</div>
      {children}
      {hint && <div className="mt-1 font-mono text-[9.5px] leading-[1.6] text-ink2">{hint}</div>}
    </div>
  )
}

// --- formatting -------------------------------------------------------------

/** A duration, as the stage plan prints one: "0.9 s", "18 m 42 s". */
export function duration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '-'
  if (ms < 1000) return `${ms} ms`
  const seconds = ms / 1000
  if (seconds < 60) return `${seconds.toFixed(1)} s`
  const minutes = Math.floor(seconds / 60)
  return `${minutes} m ${Math.round(seconds - minutes * 60)} s`
}

/** Bytes at the precision the board prints: "41.2 MB". */
export function megabytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '-'
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

/** How long ago, in the words the users table uses. */
export function ago(iso: string | null | undefined): string {
  if (!iso) return 'never'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return 'never'
  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000))
  if (seconds < 45) return 'Now'
  if (seconds < 3600) return `${Math.round(seconds / 60)} min ago`
  if (seconds < 86400) return `${Math.round(seconds / 3600)} h ago`
  if (seconds < 172800) return 'Yesterday'
  return `${Math.round(seconds / 86400)} days ago`
}

/** A clock time in the audit log's shape. */
export function clock(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleTimeString('en-GB', { hour12: false })
}

/** A date as a headline: "19 December 1990". */
export function longDate(iso: string | null | undefined): string {
  if (!iso) return 'Undated'
  const date = new Date(`${iso}T00:00:00`)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
}
